import React, { useState, useCallback, useMemo, useContext } from "react";
import { Col, Container, Row, Card, CardBody, CardHeader } from "reactstrap";
import BreadCrumb from "../../../common/BreadCrumb";
import { Button, Modal } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import HistoryContext from "../../../common/HistoryContext";
import toast from "react-hot-toast";
import "../utils/style.css";
import CardItem from "../component/cardItems";
import AddEditCompanyForm from "../component/addEditCompany";
import DetailCompany from "../component/detailCompany";
import { getCompaniesByMarchandId, deleteCompany } from "../services/companyApi";
import { useQuery, useQueryClient } from "react-query";
import CompleteCompany from "../component/completeCompany";

const Company = () => {
  const history = useContext(HistoryContext);
  const queryClient = useQueryClient();
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleteTargetRegistrationNumber, setDeleteTargetRegistrationNumber] = useState("");

  const currentuser = JSON.parse(sessionStorage.getItem('authUser'));
  const merchantId = currentuser?.user?.id;

  const { data: companies, error, isLoading } = useQuery(
    ["companies", merchantId],
    () => getCompaniesByMarchandId(merchantId),
    { enabled: !!merchantId }
  );

  const tabs = ['Tous', 'En cours', 'Compléte'];
  const [activeTab, setActiveTab] = useState('Tous');

  const handleTabChange = (tab) => setActiveTab(tab);

  const filteredCompanies = useMemo(() => {
    if (!companies) return [];
    switch (activeTab) {
      case 'En cours':
        return companies.filter(v => v.kycValidated === false);
      case 'Compléte':
        return companies.filter(v => v.kycValidated === true);
      default:
        return companies;
    }
  }, [companies, activeTab]);

  const toggleAddEditModal = useCallback((company = null) => {
    setSelectedCompany(company);
    setIsAddEditModalOpen(prev => !prev);
    if (isDetailModalOpen) setIsDetailModalOpen(false);
    if (isCompleteModalOpen) setIsCompleteModalOpen(false);
  }, [isDetailModalOpen, isCompleteModalOpen]);

  const handleEdit = (company) => {
    toggleAddEditModal(company);
  };

  const toggleDetailModal = useCallback((company = null) => {
    setSelectedCompany(company);
    setIsDetailModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    if (isCompleteModalOpen) setIsCompleteModalOpen(false);
  }, [isAddEditModalOpen, isCompleteModalOpen]);

  const handleDetail = (company) => {
    toggleDetailModal(company);
  };

  const toggleCompleteModal = useCallback((company = null) => {
    setSelectedCompany(company);
    setIsCompleteModalOpen(prev => !prev);
    if (isAddEditModalOpen) setIsAddEditModalOpen(false);
    if (isCompleteModalOpen) setIsCompleteModalOpen(false);
  }, [isAddEditModalOpen, isDetailModalOpen]);

  const handleComplete = (company) => {
    toggleCompleteModal(company);
  };

  const handleDelete = async (merchantId,companyId) => {
    try {
      await deleteCompany(merchantId, companyId);
      await queryClient.invalidateQueries("companies");
      toast.success("L'entreprise a été supprimée avec succès 🎉");
    } catch (error) {
      console.error("Error deleting company:", error || error?.message);
      toast.error(`Erreur lors de la suppression de l'entreprise : ${error?.message || error}`);
    } finally {
      setDeleteConfirmModal(false);
    }
  };

  const openDeleteConfirmModal = (companyId) => {
    const companyToDelete = companies.find(company => company.id === companyId);
    if (companyToDelete) {
      setDeleteTargetId(companyId);
      setDeleteTargetRegistrationNumber(companyToDelete.registrationNumber);
      setDeleteConfirmModal(true);
    }
  };

  const closeDeleteConfirmModal = () => {
    setDeleteConfirmModal(false);
  };

  const renderTabs = () => (
    <div className="d-flex gap-3 mb-3">
      {tabs.map((tab) => (
        <Button
          key={tab}
          onClick={() => handleTabChange(tab)}
          className={`btn ${activeTab === tab ? 'active-tab' : 'btn-light'}`}
        >
          {tab}
        </Button>
      ))}
    </div>
  );

  const HeaderTable = () => (
    <div className="d-flex justify-content-between align-items-center w-100">
      <div className="d-flex gap-3">
        {renderTabs()}
      </div>
      <Button
        type="button"
        className="active-tab"
        icon={<PlusCircleOutlined />}
        title="Ajouter une entreprise"
        onClick={() => toggleAddEditModal(null)}
      >
        Ajouter une entreprise
      </Button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb page="Entreprise" history={history} />
          <Row>
            <Col>
              <Card id="contactList">
                <CardHeader>
                  <HeaderTable />
                </CardHeader>
                <CardBody className="pt-0">
                  <Row>
                    {filteredCompanies?.map((company) => (
                      <Col lg={4} key={company.id}>
                        <CardItem
                          companies={[company]}
                          onDetail={handleDetail}
                          onEdit={handleEdit}
                          onComplete={handleComplete}
                          onDelete={openDeleteConfirmModal}
                          setIsCompleteModalOpen={setIsCompleteModalOpen}
                        />
                      </Col>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <AddEditCompanyForm
            modal={isAddEditModalOpen}
            setModal={setIsAddEditModalOpen}
            selectedCompany={selectedCompany}
            toggle={toggleAddEditModal}
            currentuser={currentuser}
          />
          
          <DetailCompany
            modal={isDetailModalOpen}
            setModal={setIsDetailModalOpen}
            selectedCompany={selectedCompany}
            toggle={toggleDetailModal}
            currentuser={currentuser}
          />
          
          <CompleteCompany
            modal={isCompleteModalOpen}
            setModal={setIsCompleteModalOpen}
            selectedCompany={selectedCompany}
            toggle={toggleCompleteModal}
            currentuser={currentuser}
          />

          <Modal
            title="Confirmer la suppression de l'entreprise"
            visible={deleteConfirmModal}
            onCancel={closeDeleteConfirmModal}
            footer={[
              <Button key="cancel" onClick={closeDeleteConfirmModal} className="btn btn-soft-primary">
                Annuler
              </Button>,
              <Button key="delete" type="danger" className="active-tab" onClick={() => handleDelete(merchantId,deleteTargetId)}>
                Oui, Supprime
              </Button>,
            ]}
          >
            <p>Êtes-vous sûr de vouloir supprimer l'entreprise {deleteTargetRegistrationNumber} ?</p>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Company;
